import React, { useCallback, useEffect, useMemo } from "react";
import { Button, Col, Form, Label } from "reactstrap";
import { cpf } from 'cpf-cnpj-validator';
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { pt } from 'yup-locale-pt'

import { withTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import replaceAll from "./replaceAll";

import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { InputTypeAditionalFields } from "./InputTypeAditionalFields";

Yup.setLocale(pt)

const generateAdditionalFieldName = (additionalField) => {
  return `${replaceAll(`${additionalField.id}`, '-', '_')}_${replaceAll(`${additionalField.name}`, ' ', '')}`;
}

const AditionalFieldsForm = ({
  t,
  data,
  onSaveValues,
  isLoading,
  hasPermissionAditionalFieldsWrite = false,
}) => {
  toastr.options = {
    closeButton: false,
    debug: false,
    newestOnTop: false,
    progressBar: true,
    positionClass: "toast-top-right",
    preventDuplicates: true,
    onclick: null,
    showDuration: "200",
    hideDuration: "200",
    timeOut: "2000",
    extendedTimeOut: "2000",
    showEasing: "swing",
    hideEasing: "linear",
    showMethod: "fadeIn",
    hideMethod: "fadeOut",
  };

  const additionalFields = useMemo(() => {
    return data?.filter(Boolean).map((additionalField) => ({
      ...additionalField,
      nameFieldForm: `${generateAdditionalFieldName(additionalField)}`
    })) ?? [];
  }, [data]);

  const {
    control,
    formState,
    register,
    handleSubmit,
    setValue,
  } = useForm({
    reValidateMode: 'onChange',
    resolver: yupResolver(Yup.object(additionalFields.reduce((shape, additionalField) => {
      let schema = (() => {
        switch (additionalField.type) {
          case 'cpf': return Yup.string().test(value => cpf.isValid(value))
          case 'text': {
            let schema = Yup.string()

            if (additionalField.configuration.minLength) schema = schema.min(additionalField.configuration.minLength)
            if (additionalField.configuration.maxLength) schema = schema.max(additionalField.configuration.maxLength)

            return schema
          }
          case 'money': return Yup.string()
          case 'phone': return Yup.string()
          default: return Yup.mixed()
        }
      })()

      schema = schema.label(additionalField.name).nullable(false)

      if (additionalField.required) schema = schema.required()

      return {
        ...shape,
        [generateAdditionalFieldName(additionalField)]: schema
      }
    }, {}))),
    defaultValues: additionalFields.reduce((defaultValues, additionalField) => ({
      ...defaultValues,
      [generateAdditionalFieldName(additionalField)]: additionalField.value
    }), {}),
  });  

  const setClassName = (errors) => {
    return errors ? "av-invalid is-invalid form-control" : "form-control";
  };

  const showToast = useCallback((type, message) => {
    toastr[type](message);
  }, []);

  useEffect(()=>{
    
    if(additionalFields?.length > 0){

      additionalFields.forEach((ele)=>{
        setValue(ele?.nameFieldForm, ele?.value);   
      });

    }
    
  },[additionalFields, setValue]);

  const onSubmit = useCallback((dataSubmit) => {
    if(hasPermissionAditionalFieldsWrite){
      onSaveValues(additionalFields.map(({nameFieldForm, ...ele},_)=>{
        const value = dataSubmit[nameFieldForm] ?? null;
        return {...ele, value};
      }));
    }
  }, [onSaveValues, additionalFields, hasPermissionAditionalFieldsWrite]);

  if (!data || data?.length === 0) {
    return (
      <div class="d-flex justify-content-center pt-5">
        <div className="align-self-center">
          <p className="text-center">{t('There are no additional fields configured for this skill')}</p>
        </div>
      </div>
    );
  }

  return (
    <Form style={{ overflow: 'auto', minHeight: '100%' }} onSubmit={handleSubmit(onSubmit)}>
      {
        additionalFields.map((additionalField) => (
          <Col className='pl-2 pr-2 pt-2' key={`aditional-fields:${additionalField.id}`}>
            <Label for={`${additionalField.nameFieldForm}`}>
              {additionalField.name ?? '--'}{' '}
              {additionalField.required && <span style={{ color: '#F46A6A' }}>*</span>}
            </Label>
            <InputTypeAditionalFields
              item={additionalField}
              className={setClassName(formState.errors[`${additionalField.nameFieldForm}`])}
              register={register}
              disabled={!hasPermissionAditionalFieldsWrite}
              control={control}
            />
            {
              additionalField.configuration?.textHelp && (
                <div className="pt-2" style={{ fontSize: '10.5px' }}>
                  {additionalField.configuration?.textHelp}
                </div>
              )
            }
            {
              formState.errors[`${additionalField.nameFieldForm}`] && (
                <div className="invalid-feedback d-block">
                  {formState.errors[`${additionalField.nameFieldForm}`].message}
                </div>
              )
            }
          </Col>
        ))
      }
      <div className="d-flex justify-content-end mb-3 mt-3">
        <Button
          type="submit"
          color="primary"
          disabled={isLoading || !hasPermissionAditionalFieldsWrite}
          className="mr-2 waves-effect waves-light"
          style={{ cursor: hasPermissionAditionalFieldsWrite ? 'pointer' : 'not-allowed' }}
        >
          {isLoading && (
            <div class="spinner-border spinner-border-sm mr-2" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          )}
          {t("Save")}
        </Button>
      </div>
    </Form>
  );

};

export default withTranslation()(AditionalFieldsForm);
